import { useMemo } from 'react';
import { INVESTMENT_TYPE_LABELS } from './constants';

// Prefer display_value (converted to user's display currency) when present.
// Falls back to current_value → amount, matching backend behaviour exactly.
const valueOf = (inv) => (
  (inv && inv.display_value != null) ? inv.display_value
  : (inv?.current_value || inv?.amount || 0)
);

export function useDashboardChartData(portfolioSummary, investments) {
  const typeChartData = portfolioSummary?.by_type
    ? Object.entries(portfolioSummary.by_type).map(([type, data]) => ({
        name: type.replace('_', ' ').toUpperCase(),
        value: data.value,
        count: data.count
      })).filter(item => item.value > 0)
    : [];

  const currencyChartData = portfolioSummary?.by_currency
    ? Object.entries(portfolioSummary.by_currency).map(([currency, data]) => ({
        name: currency,
        value: data.value,
        count: data.count
      }))
    : [];

  const ownershipChartData = useMemo(() => {
    if (!investments || investments.length === 0) return [];
    const map = {
      direct: { name: 'Direct', value: 0, color: '#10B981' },
      indirect: { name: 'Indirect', value: 0, color: '#3B82F6' },
      related: { name: 'Related Assets', value: 0, color: '#8B5CF6' }
    };
    investments.forEach(inv => {
      const ownerType = inv.owner_type || 'direct';
      const value = valueOf(inv);
      if (map[ownerType]) map[ownerType].value += value;
    });
    return Object.values(map).filter(item => item.value > 0);
  }, [investments]);

  const countryChartData = useMemo(() => {
    if (!investments || investments.length === 0) return [];
    const countryMap = {};
    investments.forEach(inv => {
      const country = inv.country || inv.property_country || 'Unknown';
      const value = valueOf(inv);
      if (!countryMap[country]) countryMap[country] = { name: country, value: 0 };
      countryMap[country].value += value;
    });
    return Object.values(countryMap).filter(item => item.value > 0).sort((a, b) => b.value - a.value);
  }, [investments]);

  const liquidityChartData = useMemo(() => {
    if (!investments || investments.length === 0) return [];
    const map = {
      high: { name: 'High Liquidity', value: 0, color: '#10B981' },
      medium: { name: 'Medium Liquidity', value: 0, color: '#F59E0B' },
      low: { name: 'Low Liquidity', value: 0, color: '#EF4444' }
    };
    // Liquidity classification follows industry convention (IFRS 9 cash-equivalents
    // definition + CFA portfolio classification). High = days to cash. Low = months+
    // with restrictions. Medium covers everything in between.
    // Note: T-bills are HIGH (cash-equivalent <3m residual maturity).
    //       Fixed deposits are LOW (locked until maturity, early-exit penalties).
    const highTypes = ['bank_balance', 'treasury_bill', 'mutual_fund', 'etf', 'quoted_stocks', 'crypto'];
    const lowTypes = ['fixed_deposit', 'private_equity', 'real_estate', 'convertible', 'safe'];
    investments.forEach(inv => {
      const value = valueOf(inv);
      let level = 'medium';
      if (highTypes.includes(inv.type)) level = 'high';
      else if (lowTypes.includes(inv.type)) level = 'low';
      map[level].value += value;
    });
    return Object.values(map).filter(item => item.value > 0);
  }, [investments]);

  const durationChartData = useMemo(() => {
    if (!investments || investments.length === 0) return [];
    const map = {
      short: { name: 'Short-term (<1 year)', value: 0, color: '#3B82F6' },
      medium: { name: 'Medium-term (1-5 years)', value: 0, color: '#F59E0B' },
      long: { name: 'Long-term (>5 years)', value: 0, color: '#8B5CF6' }
    };
    const now = new Date();
    const oneYear = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
    const fiveYears = new Date(now.getFullYear() + 5, now.getMonth(), now.getDate());
    const shortTypes = ['bank_balance', 'treasury_bill', 'fixed_deposit'];
    const mediumTypes = ['mutual_fund', 'etf', 'quoted_stocks', 'crypto'];
    investments.forEach(inv => {
      const value = valueOf(inv);
      let durationType = 'long';
      if (inv.maturity_date) {
        const maturity = new Date(inv.maturity_date);
        if (maturity < oneYear) durationType = 'short';
        else if (maturity < fiveYears) durationType = 'medium';
      } else {
        if (shortTypes.includes(inv.type)) durationType = 'short';
        else if (mediumTypes.includes(inv.type)) durationType = 'medium';
      }
      map[durationType].value += value;
    });
    return Object.values(map).filter(item => item.value > 0);
  }, [investments]);

  // Counterparty concentration — groups investments by the entity that actually
  // holds / issues the asset. Field used depends on investment type:
  //   bonds / treasury_bill   → issuer_name
  //   mutual_fund / private_equity → fund_manager
  //   everything else (stocks, FDs, crypto, etc.) → institution
  //   real_estate, gold, related-assets, types with no counterparty concept
  //                                       → 'Self-custodied / Direct'
  //   missing required field              → 'Unspecified'
  const counterpartyChartData = useMemo(() => {
    if (!investments || investments.length === 0) return [];
    const SELF_CUSTODIED_LABEL = 'Self-custodied / Direct';
    const UNSPECIFIED_LABEL = 'Unspecified';
    const SELF_CUSTODIED_TYPES = new Set(['real_estate', 'gold', 'other']);
    const ISSUER_TYPES = new Set(['government_bond', 'corporate_bond', 'treasury_bill', 'convertible', 'safe']);
    const FUND_MANAGER_TYPES = new Set(['mutual_fund', 'private_equity']);

    const pickCounterparty = (inv) => {
      // Related assets (memorandum-only) are excluded from counterparty risk.
      if (inv.owner_type === 'related') return null;
      if (SELF_CUSTODIED_TYPES.has(inv.type)) return SELF_CUSTODIED_LABEL;
      if (ISSUER_TYPES.has(inv.type)) {
        return (inv.issuer_name && inv.issuer_name.trim())
          || (inv.institution && inv.institution.trim())
          || UNSPECIFIED_LABEL;
      }
      if (FUND_MANAGER_TYPES.has(inv.type)) {
        return (inv.fund_manager && inv.fund_manager.trim())
          || (inv.institution && inv.institution.trim())
          || UNSPECIFIED_LABEL;
      }
      return (inv.institution && inv.institution.trim()) || UNSPECIFIED_LABEL;
    };

    const map = {};
    let total = 0;
    investments.forEach(inv => {
      const cp = pickCounterparty(inv);
      if (!cp) return;
      const value = valueOf(inv);
      if (!map[cp]) {
        map[cp] = {
          name: cp,
          value: 0,
          count: 0,
          isThirdParty: cp !== SELF_CUSTODIED_LABEL && cp !== UNSPECIFIED_LABEL,
          isUnspecified: cp === UNSPECIFIED_LABEL,
        };
      }
      map[cp].value += value;
      map[cp].count += 1;
      total += value;
    });

    const rows = Object.values(map)
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);

    // Percent of total portfolio (all counterparty rows including self-custodied).
    // Use a non-colliding key — Recharts has its own `percent` field on label args.
    rows.forEach(r => { r.pctOfTotal = total > 0 ? (r.value / total) * 100 : 0; });
    return rows;
  }, [investments]);

  // Concentration risk threshold — fixed at 25% per product spec.
  // Self-custodied assets and "Unspecified" rows are excluded from the warning:
  //   - Self-custodied has no third-party counterparty failure mode.
  //   - "Unspecified" is a data-quality nudge, not a real concentration in
  //     a single counterparty (multiple unnamed counterparties may sit inside).
  const COUNTERPARTY_CONCENTRATION_THRESHOLD = 25;
  const thirdPartyRows = counterpartyChartData.filter(r => r.isThirdParty);
  const topThirdPartyCounterparty = thirdPartyRows[0] || null;
  const topCounterparty = counterpartyChartData[0] || null;
  const topConcentrationPct = topThirdPartyCounterparty ? topThirdPartyCounterparty.pctOfTotal : 0;
  const isConcentrated = topConcentrationPct >= COUNTERPARTY_CONCENTRATION_THRESHOLD;
  const unspecifiedRow = counterpartyChartData.find(r => r.isUnspecified);
  const unspecifiedPct = unspecifiedRow ? unspecifiedRow.pctOfTotal : 0;

  const allTypes = Object.keys(INVESTMENT_TYPE_LABELS);
  const completeTypeChartData = allTypes.map((type) => {
    const existing = portfolioSummary?.by_type?.[type];
    return {
      type,
      name: type.replace('_', ' ').toUpperCase(),
      value: existing?.value || 0,
      count: existing?.count || 0
    };
  });

  return {
    typeChartData, currencyChartData, ownershipChartData,
    countryChartData, liquidityChartData, durationChartData,
    counterpartyChartData, topCounterparty, topThirdPartyCounterparty,
    topConcentrationPct, isConcentrated, unspecifiedPct,
    completeTypeChartData
  };
}
